import { useForm } from 'react-hook-form';
import { mdiAlertCircleOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { useGetPerson } from '@my-pages/hooks/useGetPerson.ts';
import { usePutApiV2Member } from '@shared/integrations/taf-api/v2/features/member/member.ts';
import { UpdatePersonRequest } from '@shared/src/js/integrations/taf-api/v2/models';
import AlertBox from '@shared/components/AlertBox.tsx';

interface IContactFormProps {
    closeForm: () => void;
}

interface IFormFields {
    firstname: string;
    lastname: string;
    emailaddress1: string;
    mobilephone: string;
    telephone2: string;
    address1_line1: string;
    address1_line2: string;
    address1_line3: string;
    postalCode: string;
    city: string;
}

const ContactForm = ({ closeForm }: IContactFormProps) => {
    const person = useGetPerson();
    const { mutate, isError, isPending } = usePutApiV2Member();

    const {
        register,
        handleSubmit,
        formState: { isValid, errors },
    } = useForm<IFormFields>({
        defaultValues: {
            firstname: person?.firstName ?? '',
            lastname: person?.lastName ?? '',
            emailaddress1: person?.email ?? '',
            mobilephone: person?.mobilePhone ?? '',
            telephone2: person?.phone ?? '',
            address1_line1: person?.addresses && person.addresses[0] ? (person.addresses[0].street ?? '') : '',
            address1_line2: person?.addresses && person.addresses[1] ? (person.addresses[1].street ?? '') : '',
            address1_line3: person?.addresses && person.addresses[0] ? (person.addresses[0].careOf ?? '') : '',
            postalCode: person?.addresses && person.addresses[0] ? (person.addresses[0].zipCode ?? '') : '',
            city: person?.addresses ? (person?.addresses[0].city ?? '') : '',
        },
    });

    const submit = async (data: IFormFields) => {
        if (!isValid) return;
        const updatePersonRequest: UpdatePersonRequest = {
            emailAddress1: data.emailaddress1,
            telephone2: data.telephone2,
            mobilePhone: data.mobilephone,
        };
        mutate(
            { data: updatePersonRequest },
            {
                onSuccess: () => {
                    closeForm();
                },
            },
        );
    };

    return (
        <form
            onSubmit={handleSubmit(submit)}
            className='fade-in-medium'
        >
            {isError ? (
                <AlertBox
                    className='mb-4'
                    theme='danger'
                >
                    <h5 className='alert-heading'>Något gick fel</h5>
                    <p>Var vänlig och prova igen.</p>
                    <p>
                        Vid akuta frågor kontakta oss på:{' '}
                        <a
                            href='tel:010-480 30 00'
                            className='text-nowrap'
                        >
                            010-480 30 00
                        </a>
                        .
                    </p>
                </AlertBox>
            ) : null}
            <p className='required-info'>* Obligatoriska uppgifter</p>
            <div className='form-group row'>
                <label
                    htmlFor='profileFirstName'
                    className='col-md-3 col-form-label label-sm'
                >
                    Förnamn
                </label>
                <div className='col-md-9'>{person?.firstName ?? <span>&mdash;</span>}</div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='profileLastName'
                    className='col-md-3 col-form-label label-sm'
                >
                    Efternamn
                </label>
                <div className='col-md-9'>{person?.lastName ?? <span>&mdash;</span>}</div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='profileEmail'
                    className='col-md-3 col-form-label label-sm label-required'
                >
                    E-postadress
                </label>
                <div className='col-md-9'>
                    <input
                        {...register('emailaddress1', { required: true, pattern: /.+@.+\..+/ })}
                        type='email'
                        id='profileEmail'
                        className='form-control'
                        autoComplete='email'
                    />
                    {errors.emailaddress1 && (
                        <div className='validation-message slide-up-fade-in'>
                            <Icon
                                path={mdiAlertCircleOutline}
                                className='mdi mdi-18 mr-1'
                            />
                            <span className='validation-text'>Ogiltig e-postadress</span>
                        </div>
                    )}
                </div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='profileTelMobile'
                    className='col-md-3 col-form-label label-sm label-required'
                >
                    Mobiltelefon
                </label>
                <div className='col-md-9'>
                    <input
                        {...register('mobilephone', {
                            required: true,
                            pattern: /^[0-9+\-() ]{6,}$/,
                        })}
                        type='tel'
                        id='profileTelMobile'
                        className='form-control'
                        autoComplete='tel'
                    />
                    {errors.mobilephone && (
                        <div className='validation-message slide-up-fade-in'>
                            <Icon
                                path={mdiAlertCircleOutline}
                                className='mdi mdi-18 mr-1'
                            />
                            <span className='validation-text'>Ogiltigt telefonnummer</span>
                        </div>
                    )}
                </div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='profileTelHome'
                    className='col-md-3 col-form-label label-sm'
                >
                    Hemtelefon
                </label>
                <div className='col-md-9'>
                    <input
                        {...register('telephone2', {
                            required: false,
                            pattern: /^[0-9+\-() ]*$/,
                        })}
                        type='tel'
                        id='profileTelHome'
                        name='telephone2'
                        className='form-control'
                        autoComplete='tel'
                    />
                    {errors.telephone2 && (
                        <div className='validation-message slide-up-fade-in'>
                            <Icon
                                path={mdiAlertCircleOutline}
                                className='mdi mdi-18 mr-1'
                            />
                            <span className='validation-text'>Ogiltigt telefonnummer</span>
                        </div>
                    )}
                </div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='profileAddress1'
                    className='col-md-3 col-form-label label-sm'
                >
                    Adress
                </label>
                <div className='col-md-9'>{person?.addresses && person.addresses[0] ? (person.addresses[0].street ?? <span>&mdash;</span>) : <span>&mdash;</span>}</div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='profileAddress2'
                    className='col-md-3 col-form-label label-sm'
                >
                    Adressrad 2
                </label>
                <div className='col-md-9'>{person?.addresses && person.addresses[1] ? (person.addresses[1].street ?? <span>&mdash;</span>) : <span>&mdash;</span>}</div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='profileCoAddress'
                    className='col-md-3 col-form-label label-sm'
                >
                    C/o adress
                </label>
                <div className='col-md-9'>{person?.addresses && person.addresses[0] ? (person.addresses[0].careOf ?? <span>&mdash;</span>) : <span>&mdash;</span>}</div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='profilePostcode'
                    className='col-md-3 col-form-label label-sm'
                >
                    Postnummer
                </label>
                <div className='col-md-9'>{person?.addresses && person.addresses[0] ? (person.addresses[0].zipCode ?? <span>&mdash;</span>) : <span>&mdash;</span>}</div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='profileCity'
                    className='col-md-3 col-form-label label-sm'
                >
                    Postort
                </label>
                <div className='col-md-9'>{person?.addresses && person?.addresses[0] ? (person.addresses[0].city ?? <span>&mdash;</span>) : <span>&mdash;</span>}</div>
            </div>
            <div className='d-flex justify-content-sm-end mt-5 mb-4'>
                {isPending ? (
                    <div className='m-3 spinner-lg spinner-linear spinner'></div>
                ) : (
                    <>
                        <button
                            className='btn btn-light btn-lg mr-3'
                            type='button'
                            onClick={() => closeForm()}
                        >
                            Avbryt
                        </button>
                        <button className='btn btn-primary btn-lg'>Spara</button>
                    </>
                )}
            </div>
        </form>
    );
};

export default ContactForm;
